import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useXHR } from "@sembark-travel/xhr"
import { useMounted } from "@sembark-travel/ui-base"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ColumnExportInDialog, DateRangeDialog } from "./ColumnExport"
import { useDialog } from "@sembark-travel/ui-dialog"
import { TemporaryStorage } from "../TemporaryStorage"
import config from "../config"

export { TemporaryStorage }

type BaseChildrenProps = {
	isDownloading: boolean
}

type DirectChildrenProps = BaseChildrenProps & {
	triggerDownload: () => void
}

type DateRangeChildrenProps = BaseChildrenProps & {
	openDateRangeDialog: () => void
	dateRange: { from?: Date; to?: Date }
}

type CustomColumnsChildrenProps = BaseChildrenProps & {
	open: (modalType: "customColumn" | "presets") => void
}

type CustomColumnsWithDateRangeChildrenProps = BaseChildrenProps & {
	open: (modalType: "customColumn" | "presets") => void
	dateRange: { from?: Date; to?: Date }
}

type BaseProps = {
	downloadUrl: string
	fileName?: string
	title?: string
	options?: {
		showDateRangeDialogSeparately?: boolean
	}
}

export type DirectModeProps = BaseProps & {
	mode: "direct"
	getRequestParams: () => Record<string, unknown>
	children: (props: DirectChildrenProps) => React.ReactNode
}

export type DateRangeModeProps = BaseProps & {
	mode: "dateRange"
	startDate: Date
	endDate: Date
	getRequestParams: (startDate: Date, endDate: Date) => Record<string, unknown>
	children: (props: DateRangeChildrenProps) => React.ReactNode
}

export type CustomColumnsModeProps = BaseProps & {
	mode: "customColumns"
	reportType: string
	getRequestParams: () => Record<string, unknown>
	children: (props: CustomColumnsChildrenProps) => React.ReactNode
}

export type CustomColumnsWithDateRangeModeProps = BaseProps & {
	mode: "customColumnsWithDateRange"
	reportType: string
	startDate?: Date
	endDate?: Date
	getRequestParams: (startDate: Date, endDate: Date) => Record<string, unknown>
	children: (props: CustomColumnsWithDateRangeChildrenProps) => React.ReactNode
}
export type DownloadManagerProps =
	| DirectModeProps
	| DateRangeModeProps
	| CustomColumnsModeProps
	| CustomColumnsWithDateRangeModeProps

/**
 * Unified download component that handles:
 * - Direct downloads
 * - Date range downloads (using DateRangeDialog)
 * - Custom column downloads (using ColumnExportInDialog)
 * - Combined custom columns which allows to edit date range
 */
export function DownloadManager(props: DownloadManagerProps) {
	const { mode, downloadUrl, fileName, title, children } = props
	const startDate = "startDate" in props ? props.startDate : undefined
	const endDate = "endDate" in props ? props.endDate : undefined
	const reportType = "reportType" in props ? props.reportType : undefined

	// Initialize the date range
	const initialDateRange = useMemo(() => {
		if (startDate && endDate) {
			return {
				from: startDate,
				to: endDate,
			}
		}
		return undefined
	}, [startDate, endDate])

	const xhr = useXHR()
	const mounted = useMounted()
	const [isDateRangeDialogOpen, openDateRangeDialog, closeDateRangeDialog] =
		useDialog()

	// Unified download state
	const [storageId, setStorageId] = useState<string | undefined>()
	const [isDownloading, setIsDownloading] = useState(false)

	// Handle file download when URL is ready
	const handleFileDownload = useCallback(
		(fileUrl: string) => {
			if (!fileUrl) return

			const link = document.createElement("a")
			link.href = fileUrl
			link.download =
				fileName || `download-${new Date().toISOString().split("T")[0]}.xlsx`
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)

			// Reset download state after file is downloaded
			setTimeout(() => {
				setStorageId(undefined)
				setIsDownloading(false)
				closeDateRangeDialog()
			}, 500)
		},
		[fileName, closeDateRangeDialog]
	)

	// Poll for file URL when storageId is set
	useEffect(() => {
		if (!storageId) return

		let pollCount = 0
		const maxPolls = 900 // 2 seconds per poll, 30 minute timeout

		const interval = setInterval(async () => {
			pollCount++

			if (pollCount > maxPolls) {
				showSnackbar("Download timed out. Please try again.")
				setIsDownloading(false)
				setStorageId(undefined)
				clearInterval(interval)
				return
			}

			try {
				const response = await xhr.get(`temporary-storage/${storageId}`)
				const data = response.data.data

				if (data.url && mounted()) {
					// File is ready, stop polling and download
					setStorageId(undefined)
					setIsDownloading(false)
					clearInterval(interval)
					handleFileDownload(data.url)
				} else if (data.status === "failed" && mounted()) {
					showSnackbar("Download failed. Please try again.")
					setIsDownloading(false)
					setStorageId(undefined)
					clearInterval(interval)
				}
			} catch (error) {
				showSnackbar("Error checking download status. Please try again.")
				clearInterval(interval)
				setIsDownloading(false)
				setStorageId(undefined)
			}
		}, 2000)

		return () => clearInterval(interval)
	}, [storageId, xhr, mounted, handleFileDownload])

	// Unified download handler - works for direct, date range, and custom columns
	const handleDownload = useCallback(
		async (downloadParams?: {
			dateRange?: { from: Date; to: Date }
			headings?: Array<{ slug: string }>
		}) => {
			if (isDownloading) return

			setIsDownloading(true)
			try {
				let baseParams: Record<string, unknown>

				if (props.mode === "direct" || props.mode === "customColumns") {
					// Modes without date range: getRequestParams will not receive dates
					baseParams = props.getRequestParams()
				} else {
					// Modes with date range: getRequestParams will receive both from and to
					const from = downloadParams?.dateRange?.from
					const to = downloadParams?.dateRange?.to

					if (!from || !to) {
						setIsDownloading(false)
						return
					}

					baseParams = props.getRequestParams(from, to)
				}

				const payload: Record<string, unknown> = {
					...baseParams,
					...(config.timezoneOffset !== undefined
						? {
								timezone_offset: config.timezoneOffset,
							}
						: {}),
					...(downloadParams?.headings
						? {
								headings: downloadParams.headings,
							}
						: {}),
				}

				const response = await xhr.get(downloadUrl, {
					params: payload,
				})

				if (response.data?.data?.id) {
					setStorageId(response.data.data.id)
				} else if (response.data?.message) {
					alert(response.data.message)
					setIsDownloading(false)
					closeDateRangeDialog()
				} else {
					throw new Error("No storage ID received")
				}
			} catch (error: unknown) {
				const apiError = error as {
					message?: string
					response?: { data?: { errors?: unknown } }
				}
				showSnackbar(
					apiError?.message || "Failed to start download. Please try again."
				)
				setIsDownloading(false)
			}
		},
		[isDownloading, xhr, closeDateRangeDialog, props, downloadUrl]
	)

	// Handle date range download
	const handleDateRangeDownload = useCallback(
		async (from: Date, to: Date) => {
			if (!from || !to) return
			await handleDownload({
				dateRange: {
					from: from,
					to: to,
				},
			})
		},
		[handleDownload]
	)

	// Handle custom columns download request
	const handleCustomColumnsDownload = useCallback(
		({
			headings,
			dateRange,
		}: {
			headings?: Array<{ slug: string }>
			dateRange?: { from: Date; to: Date }
		}) => {
			handleDownload({
				dateRange:
					dateRange?.from && dateRange?.to
						? { from: dateRange.from, to: dateRange.to }
						: undefined,
				headings,
			})
		},
		[handleDownload]
	)

	// Render based on mode
	const renderContent = () => {
		// For direct download mode
		if (mode === "direct") {
			return children({
				isDownloading,
				triggerDownload: () => handleDownload(),
			})
		}

		// For date range mode
		if (mode === "dateRange") {
			return (
				<>
					{children({
						openDateRangeDialog,
						dateRange: initialDateRange || { from: undefined, to: undefined },
						isDownloading,
					})}
					<DateRangeDialog
						title={title || fileName || "Custom Date Export"}
						open={isDateRangeDialogOpen}
						initialFrom={initialDateRange?.from}
						initialTo={initialDateRange?.to}
						onClose={closeDateRangeDialog}
						onSave={handleDateRangeDownload}
						saveButtonText={isDownloading ? "Downloading..." : "Download"}
						disableSaveButton={isDownloading}
					/>
				</>
			)
		}

		// For custom columns mode (with or without date range)
		if (
			(mode === "customColumns" || mode === "customColumnsWithDateRange") &&
			reportType
		) {
			return (
				<ColumnExportInDialog
					reportType={reportType}
					title={title}
					allowDatesChange={
						mode === "customColumnsWithDateRange" ? true : false
					}
					startDate={
						mode === "customColumnsWithDateRange"
							? initialDateRange?.from
							: undefined
					}
					endDate={
						mode === "customColumnsWithDateRange"
							? initialDateRange?.to
							: undefined
					}
					onDownload={handleCustomColumnsDownload}
					isGenerating={isDownloading}
				>
					{({ open }) => {
						return children({
							open,
							isDownloading,
							dateRange: initialDateRange || { from: undefined, to: undefined },
						})
					}}
				</ColumnExportInDialog>
			)
		}

		// Fallback - just render children
		return children
	}

	return <>{renderContent()}</>
}
